/**
 * Synchronous Auth State for TanStack Router beforeLoad
 *
 * Provides synchronous auth checks that read directly from localStorage.
 * This avoids stale closure issues with React Context in beforeLoad guards.
 *
 * @module utils/authState
 */

import { getPermissionsSync } from './permissionCache';
import { findAuth0StorageKey } from './authUtils';
import { STORAGE_KEYS } from '../sessionConfig';

/**
 * @typedef {Object} AuthStateSync
 * @property {boolean} isAuthenticated - Whether user is authenticated
 * @property {string|null} token - Access token or null
 * @property {Set<string>} permissions - User permissions set
 */

/**
 * Synchronous auth check for beforeLoad.
 * Reads directly from localStorage to avoid React Context stale closures.
 *
 * @returns {AuthStateSync} Current auth state
 *
 * @example
 * ```js
 * const { isAuthenticated, token, permissions } = getAuthStateSync();
 * if (!isAuthenticated) {
 *   throw redirect({ to: '/login' });
 * }
 * ```
 */
export function getAuthStateSync() {
  try {
    // Read auth config to determine provider and storage key
    const configStr = localStorage.getItem(STORAGE_KEYS.AUTH_CONFIG);
    if (!configStr) {
      // No config stored yet - user hasn't logged in or app hasn't initialized
      return { isAuthenticated: false, token: null, permissions: new Set() };
    }

    const config = JSON.parse(configStr);
    const { provider, oidcAuth } = config;
    if (!oidcAuth) {
      return { isAuthenticated: false, token: null, permissions: new Set() };
    }

    // GCP (Firebase) stores its ID token under a dedicated localStorage key
    if (provider === 'gcp') {
      const gcpToken = localStorage.getItem(STORAGE_KEYS.GCP_TOKEN);
      if (!gcpToken) {
        return { isAuthenticated: false, token: null, permissions: new Set() };
      }
      return { isAuthenticated: true, token: gcpToken, permissions: getPermissionsSync() };
    }

    // Construct the provider-specific storage key
    let storageKey;
    if (provider === 'auth0') {
      const { clientId, audience } = oidcAuth;
      storageKey = findAuth0StorageKey(clientId, audience);
    } else {
      // OIDC provider
      const { authority, clientId } = oidcAuth;
      storageKey = `oidc.user:${authority}:${clientId}`;
    }

    // Read token data from provider storage
    const tokenDataStr = localStorage.getItem(storageKey);
    if (!tokenDataStr) {
      return { isAuthenticated: false, token: null, permissions: new Set() };
    }

    const tokenData = JSON.parse(tokenDataStr);

    // Extract access token based on provider format
    let accessToken;
    let expiresAt;

    if (provider === 'auth0') {
      // Auth0 stores in { body: { access_token, expires_in, ... }, expiresAt }
      const body = tokenData.body || tokenData;
      accessToken = body.access_token;
      // Auth0 stores expiresAt as Unix timestamp in seconds
      expiresAt = tokenData.expiresAt ? tokenData.expiresAt * 1000 : null;
    } else {
      // OIDC stores { access_token, expires_at, ... }
      accessToken = tokenData.access_token;
      // OIDC stores expires_at as Unix timestamp in seconds
      expiresAt = tokenData.expires_at ? tokenData.expires_at * 1000 : null;
    }

    if (!accessToken) {
      return { isAuthenticated: false, token: null, permissions: new Set() };
    }

    // Check expiry
    if (expiresAt && Date.now() >= expiresAt) {
      return { isAuthenticated: false, token: null, permissions: new Set() };
    }

    // Get permissions from localStorage cache (populated after login)
    const permissions = getPermissionsSync();

    return {
      isAuthenticated: true,
      token: accessToken,
      permissions,
    };
  } catch (e) {
    console.warn('[authState] Error reading auth state:', e);
    return { isAuthenticated: false, token: null, permissions: new Set() };
  }
}

/**
 * Check if auth config exists in storage (fast check before full auth validation).
 * @returns {boolean} True if auth config exists
 */
export function hasAuthConfig() {
  try {
    return localStorage.getItem(STORAGE_KEYS.AUTH_CONFIG) !== null;
  } catch {
    return false;
  }
}

/**
 * Get the auth config storage key (for debugging/testing).
 * @returns {string} The localStorage key for auth config
 */
export function getAuthConfigKey() {
  return STORAGE_KEYS.AUTH_CONFIG;
}
